import styled from 'styled-components';

import { useClickAttachment } from 'hooks/useClickAttachment';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import Workflow, { WorkflowAttachmentOptions } from 'models/Workflow';
import { ActionStatus, WorkflowAction } from 'models/WorkflowAction';
import { WorkflowActionIntent } from 'services/api/workflows';
import colors from 'styles/theme/colors';

import { InlineLink, StepDescription } from './categoryCard/styles';
import { ReferralModalSection } from './ReferralModal';

/**
 * The completed body for the document step, which has two faces.
 *
 * Per-document processing always completes: a failed document is not an action-level error or a
 * human gate, so the lifecycle status stays `completed` and the step stays terminal. What the API
 * withholds is the value — any failed document publishes no packet value at all, which is what
 * makes the extract and the clinical assessment flag for a person instead of settling against a
 * packet nobody said was incomplete. The API folds the dispatched batch's errored jobs into an
 * `errored` EFFECTIVE status so the red lands on the step that actually failed.
 *
 * That split is why this reads `effectiveStatus`. `isCompleted` is true either way, so the success
 * line would otherwise render underneath the row's own Error heading.
 *
 * It also has to own the retry. The card's other retry routes don't reach this state: the stale
 * banner needs `stale`, and there is no generic errored affordance keyed on the presented status
 * (behavior deliberately keys off `status` — see the WorkflowAction model). `rerun` alone is the
 * whole recovery here, with no chased `dispatch`: this kind is automated, so the evaluator
 * dispatches the fresh instance, and its dispatcher rebuilds an errored per-document job rather
 * than reusing it — so the same packet really is re-read, not waved through. A clean pass publishes
 * the full list, which unflags both consumers on the next evaluator pass.
 */
export default function ProcessAttachmentsNote({
  action,
  workflow,
  onEdit,
  onInvoke,
  isInvoking,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onEdit?: (section: ReferralModalSection) => void;
  onInvoke: (actionId: string, intent: WorkflowActionIntent) => void;
  isInvoking: boolean;
}) {
  const { canEditAction } = useWorkflowPermissions();
  const failed = action.effectiveStatus === ActionStatus.ERRORED;
  const failedDocuments = workflow.attachments.filter((attachment) => attachment.processingFailed);
  const total = workflow.attachments.length;
  // Retrying the same packet is a different remedy from changing it: the documents may be right and
  // only the parse failed. Offered first for that reason — it's the cheaper thing to try, but only
  // when some failed document can succeed on a second try. A failure with no reason predates them
  // and keeps the Try again it always had. Inline links joined with ' or ', matching how a flagged
  // step offers its remedies.
  const retryable = failedDocuments.length === 0 || failedDocuments.some((doc) => doc.failure?.retryable ?? true);
  const retry = failed && retryable && canEditAction(action, workflow);

  const listed = failed && failedDocuments.length > 0;

  return (
    // A div when it holds the list, which can't sit inside a paragraph.
    <StepDescription as={listed ? 'div' : undefined}>
      {listed ? (
        <>
          {failedDocuments.length === total
            ? `None of the ${total} documents could be processed.`
            : `${failedDocuments.length} of ${total} documents couldn’t be processed.`}
          <FailedList data-testid='failed-documents'>
            {failedDocuments.map((doc) => (
              <FailedDocument key={doc.id} doc={doc} />
            ))}
          </FailedList>
        </>
      ) : failed ? (
        'Some documents couldn’t be processed. '
      ) : (
        'Documents have been successfully processed. '
      )}
      {retry && (
        <InlineLink
          type='button'
          disabled={isInvoking}
          onClick={() => onInvoke(action.id, 'rerun')}
          data-testid='process-attachments-retry'>
          Try again
        </InlineLink>
      )}
      {retry && onEdit && ' or '}
      {/* Not merely disabled — an editor-less user gets no route in at all (OLIO-2615). */}
      {onEdit && (
        <InlineLink type='button' onClick={() => onEdit('documents')} data-testid='add-documents'>
          Add or remove documents
        </InlineLink>
      )}
    </StepDescription>
  );
}

function FailedDocument({ doc }: { doc: WorkflowAttachmentOptions }) {
  const open = useClickAttachment(doc.id, 'Workflow');

  return (
    <li>
      <FailedName type='button' onClick={open} data-testid='failed-document-name'>
        {doc.filename ?? 'Untitled document'}
      </FailedName>
      <span>{doc.failure?.message ?? 'File couldn’t be processed.'}</span>
    </li>
  );
}

const FailedList = styled.ul`
  list-style: none;
  margin: 12px 0;
  padding: 0;
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  background: ${colors.white};

  li {
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    line-height: 20px;
    word-break: break-word;
  }

  li + li {
    border-top: 1px solid ${colors.black10};
  }
`;

const FailedName = styled.button`
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 500;
  letter-spacing: inherit;
  text-align: left;
  color: ${colors.blue100};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
